import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, where, type Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/PageHeader';
import { DEMO_DISCOVERY_REQUESTS } from '../lib/demoData';
import { FilterChip } from '../components/FilterChip';
import { NO_DISCOVERY_EMAILS } from '../lib/discovery';
import { DiscoveryGuests } from './admin/DiscoveryGuests';

// Discovery flight requests from the public LGB61 site (written by the site,
// alerted by onDiscoveryRequestCreated). Casey 10/20/26, option A: no free-for-all.
//   • A request made through a CFI's personal link (?cfi=<slug>) is auto-assigned
//     to that CFI by the trigger; only they (and the owner) are told.
//   • Any other request lands here; we send the guest the instructor list and the
//     guest contacts the CFI they choose. AJM never chooses or forwards.
// A CFI only ever sees requests assigned to them.

// Public LGB61 site. The book. host opens straight into the booking popup.
export const LGB61_SITE_URL = 'https://book.lgb61.com';
export const cfiSlug = (p?: { discoverySlug?: string; personal?: { firstName?: string } } | null) =>
  (p?.discoverySlug || p?.personal?.firstName || '').toLowerCase().replace(/[^a-z0-9]/g, '');

type Status = 'new' | 'claimed' | 'scheduled' | 'closed' | 'spam';

interface DiscoveryRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  date?: string;
  window?: string;
  backupDate?: string;
  slotStart?: string;
  slotEnd?: string;
  passengers?: string;
  weight?: string;
  experience?: string;
  notes?: string;
  smsConsent?: boolean;
  status: Status;
  slotOpenAtRequest?: boolean | null;
  claimedByUid?: string;
  claimedByName?: string;
  assignedBy?: 'cfi-link' | 'owner';
  createdAt?: Timestamp;
}

const STATUS_LABEL: Record<Status, string> = {
  new: 'Open',
  claimed: 'Instructor picked',
  scheduled: 'Scheduled',
  closed: 'Closed',
  spam: 'Spam',
};

function when(r: DiscoveryRequest) {
  if (r.slotStart) {
    const s = new Date(r.slotStart);
    return s.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  const d = r.date ? new Date(`${r.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Any day';
  return `${d}${r.window ? ` · ${r.window}` : ''}`;
}

export function DiscoveryRequests() {
  const { profile, isAdmin, fbUser, demoMode } = useAuth();
  const [rows, setRows] = useState<DiscoveryRequest[] | null>(null);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  // Owner: Requests (from the site) vs. Guests (signed waivers + the person behind each).
  const [view, setView] = useState<'requests' | 'guests'>('requests');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Demo: one sample request, never the live collection.
    if (demoMode) {
      setRows(DEMO_DISCOVERY_REQUESTS.map((r) => ({ ...r })) as unknown as DiscoveryRequest[]);
      return;
    }
    if (!db || !fbUser) return;
    // A CFI may only read requests assigned to them (rules enforce it), so the
    // query has to say so. Sorted client-side to avoid a composite index.
    const q = isAdmin
      ? query(collection(db, 'discoveryRequests'), orderBy('createdAt', 'desc'))
      : query(collection(db, 'discoveryRequests'), where('claimedByUid', '==', fbUser.uid));
    return onSnapshot(
      q,
      (snap) =>
        setRows(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Omit<DiscoveryRequest, 'id'>) }))
            .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
        ),
      () => setErr('Could not load requests.')
    );
  }, [demoMode, isAdmin, fbUser?.uid]);

  // No owner assignment: the guest contacts the CFI they choose from the list, and
  // that CFI books it. AJM never chooses or forwards (keeps it from looking like
  // AJM supplies the instructor with the airplane).

  const [copied, setCopied] = useState(false);
  const myLink = `${LGB61_SITE_URL}/?cfi=${cfiSlug(profile)}`;

  const myName = profile?.personal?.firstName
    ? `${profile.personal.firstName} ${profile.personal.lastName ?? ''}`.trim()
    : profile?.email ?? '';

  async function setStatus(r: DiscoveryRequest, status: Status) {
    if (demoMode) {
      setRows((cur) => (cur ?? []).map((x) => (x.id === r.id ? { ...x, status } : x)));
      return;
    }
    if (!db) return;
    setBusy(r.id);
    try {
      await updateDoc(doc(db, 'discoveryRequests', r.id), { status });
    } finally {
      setBusy(null);
    }
  }

  const visible = (rows ?? []).filter((r) =>
    isAdmin ? (filter === 'open' ? ['new', 'claimed'].includes(r.status) : true) : r.claimedByUid === fbUser?.uid
  );

  return (
    <div>
      <PageHeader
        title="Discovery flights"
        subtitle={
          isAdmin
            ? 'Requests from the LGB61 site. Ones from a CFI\u2019s personal link go straight to that CFI. For the rest, send the guest the instructor list; they contact the instructor they choose. We never choose or forward.'
            : 'Discovery flight guests who picked you. You set and collect your own instruction fee.'
        }
        actions={
          isAdmin ? (
            <div className="flex gap-2">
              <Link to="/admin/discovery/waiver" className="btn-secondary">Try the guest waiver</Link>
              <Link to="/admin/book" className="btn-secondary">Book 66X</Link>
            </div>
          ) : undefined
        }
      />

      <HowItWorksVideo />

      {!isAdmin && cfiSlug(profile) && !NO_DISCOVERY_EMAILS.includes((profile?.email || '').toLowerCase()) && (
        <div className="card mb-4 p-5">
          <h2 className="font-display text-lg font-semibold text-navy-900">Your discovery flight link</h2>
          <p className="mt-1 text-sm text-ink-muted">Share it with your own prospects. Anyone who requests a flight through it comes straight to you, and no other instructor sees it.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-slate-100 px-3 py-2 text-sm">{myLink}</code>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                navigator.clipboard?.writeText(myLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      )}

      <StudentSignupLink />

      {isAdmin && (
        <div className="card mb-4 p-5">
          <h2 className="font-display text-lg font-semibold text-navy-900">Test the whole flow</h2>
          <p className="mt-1 text-sm text-ink-muted">Walk it the way a CFI does. Owner flights are never billed, so nothing gets charged.</p>
          <ol className="mt-3 space-y-2 text-sm">
            <li><strong>1.</strong> <Link to="/admin/book?discovery=Test%20Guest" className="underline">Book a discovery flight</Link> (the box is pre-ticked).</li>
            <li><strong>2.</strong> On <Link to="/admin/bookings" className="underline">Bookings</Link>, tap <em>Guest waiver</em> on that booking and sign it on your phone. Use your own email to get the guest's copy.</li>
            <li><strong>3.</strong> <Link to="/admin/log-flight" className="underline">Log the flight</Link> on that date. It's blocked until the waiver is signed.</li>
          </ol>
        </div>
      )}

      {isAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 mr-2">
            <button type="button" onClick={() => setView('requests')} className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === 'requests' ? 'bg-white text-navy-900 shadow-sm' : 'text-ink-muted hover:text-navy-900'}`}>Requests</button>
            <button type="button" onClick={() => setView('guests')} className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === 'guests' ? 'bg-white text-navy-900 shadow-sm' : 'text-ink-muted hover:text-navy-900'}`}>Guests &amp; waivers</button>
          </div>
          {view === 'requests' && (
            <>
              <FilterChip active={filter === 'open'} onClick={() => setFilter('open')}>Open</FilterChip>
              <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterChip>
            </>
          )}
        </div>
      )}

      {isAdmin && view === 'guests' && !demoMode && <DiscoveryGuests />}
      {isAdmin && view === 'guests' && demoMode && <div className="card p-8 text-center text-ink-muted">Signed guest waivers show here on the live portal.</div>}

      {err && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>}

      {isAdmin && view === 'guests' ? null : rows === null ? (
        <div className="card p-8 text-center text-ink-muted">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No open discovery flight requests right now.</div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => {
            const mine = r.claimedByUid && r.claimedByUid === fbUser?.uid;
            return (
              <div key={r.id} className="card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-navy-900">{when(r)}</div>
                    <div className="mt-1 text-sm text-ink-muted">
                      {[r.passengers, r.experience, r.backupDate && `backup ${r.backupDate}`].filter(Boolean).join(' · ')}
                    </div>
                    {r.slotOpenAtRequest === false && (
                      <div className="mt-2 text-sm font-medium text-amber-700">That slot was taken by the time the request landed. Offer another time.</div>
                    )}
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {STATUS_LABEL[r.status] ?? r.status}
                    {r.claimedByName ? ` · ${r.claimedByName}` : ''}
                  </span>
                </div>

                {(isAdmin || mine) && (
                  <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    <div><dt className="inline text-ink-muted">Name: </dt><dd className="inline font-medium">{r.name}</dd></div>
                    <div><dt className="inline text-ink-muted">Phone: </dt><dd className="inline font-medium"><a href={`tel:${r.phone}`}>{r.phone}</a></dd></div>
                    <div><dt className="inline text-ink-muted">Email: </dt><dd className="inline font-medium"><a href={`mailto:${r.email}`}>{r.email}</a></dd></div>
                    <div><dt className="inline text-ink-muted">Weights: </dt><dd className="inline font-medium">{r.weight ? `${r.weight} lb` : 'n/a'}</dd></div>
                    {r.notes && <div className="sm:col-span-2"><dt className="inline text-ink-muted">Notes: </dt><dd className="inline">{r.notes}</dd></div>}
                    <div><dt className="inline text-ink-muted">Texts OK: </dt><dd className="inline">{r.smsConsent ? 'Yes' : 'No'}</dd></div>
                  </dl>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {!isAdmin && mine && (
                    <>
                      <p className="w-full text-sm text-ink-muted">This one's yours. Call the guest to lock a time, then book it as a discovery flight.</p>
                      <Link to={`/book?discovery=${encodeURIComponent(r.name ?? '')}`} className="btn-primary">Book discovery flight</Link>
                    </>
                  )}
                  {isAdmin && r.status === 'new' && !demoMode && (
                    <p className="w-full rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                      No instructor yet. Send the guest the instructor list. They contact the instructor they choose, and that instructor books the airplane for them. We never choose or forward to an instructor.
                    </p>
                  )}
                  {isAdmin && r.claimedByName && (
                    <p className="w-full text-xs text-ink-muted">
                      {r.assignedBy === 'cfi-link' ? `Came in through ${r.claimedByName}\u2019s link` : `Guest picked ${r.claimedByName}`}
                    </p>
                  )}
                  {isAdmin && (
                    <>
                      {r.status !== 'scheduled' && <button className="btn-secondary" disabled={busy === r.id} onClick={() => setStatus(r, 'scheduled')}>Mark scheduled</button>}
                      {r.status !== 'closed' && <button className="btn-secondary" disabled={busy === r.id} onClick={() => setStatus(r, 'closed')}>Close</button>}
                      {r.status !== 'new' && r.status !== 'spam' && !r.claimedByUid && <button className="btn-secondary" disabled={busy === r.id} onClick={() => setStatus(r, 'new')}>Reopen</button>}
                      {r.status !== 'spam' && <button className="btn-secondary" disabled={busy === r.id} onClick={() => setStatus(r, 'spam')}>Spam</button>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// The CFI training video (discovery-video/ in Dropbox, 720p cut). Open on the
// first visit, then remembers that it was seen and starts collapsed.
const VIDEO_SEEN_KEY = 'ajm_discovery_video_seen';
function HowItWorksVideo() {
  const [open, setOpen] = useState(() => {
    try {
      return !localStorage.getItem(VIDEO_SEEN_KEY);
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIDEO_SEEN_KEY, '1');
    } catch {
      /* private mode */
    }
  }, []);
  return (
    <details className="card mb-4 overflow-hidden" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary className="flex cursor-pointer items-center justify-between gap-3 p-5 font-display text-lg font-semibold text-navy-900">
        <span>How discovery flights work</span>
        <span className="text-sm font-normal text-ink-muted">2 min video</span>
      </summary>
      <div className="px-5 pb-5">
        <video
          className="w-full rounded-lg bg-slate-100"
          controls
          playsInline
          preload="metadata"
          poster="/video/discovery-flights-poster.jpg"
          src="/video/discovery-flights.mp4"
        />
      </div>
    </details>
  );
}

// After the discovery flight: the guest becomes a regular lessee with their own
// account, lease and insurance. Same link for every instructor; it's also in the
// guest's signed-waiver email.
const SIGNUP_URL = 'https://app.ajmaviation.com/register';
function StudentSignupLink() {
  const [copied, setCopied] = useState(false);
  const text = `Great flying with you! To keep training in N8366X, create your account here: ${SIGNUP_URL}`;
  return (
    <div className="card mb-4 p-5">
      <h2 className="font-display text-lg font-semibold text-navy-900">After the flight: student sign-up</h2>
      <p className="mt-1 text-sm text-ink-muted">
        Optional: if they want to keep training, send this link. They create an account and add their details. We
        review every application. If they're approved, we send their own aircraft lease, and once it's signed and their
        account is active they can reserve N8366X. Lessons, schedule and your rate stay between you and them. It's also
        in their waiver email.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="rounded-lg bg-slate-100 px-3 py-2 text-sm">{SIGNUP_URL}</code>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            navigator.clipboard?.writeText(SIGNUP_URL).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <a className="btn-secondary" href={`sms:?&body=${encodeURIComponent(text)}`}>Text it</a>
      </div>
    </div>
  );
}
